import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Card, Chip, Grid2, Stack, Typography } from '@mui/material';
import { When } from 'react-if';
import { GoogleMap, MarkerF, Circle } from '@react-google-maps/api';
import dayjs from 'dayjs';

import ReportsBackIcon from '../../assets/icons/reports-back-icon';
import { ReportTypes } from '../../types';
import { LatLng } from '../event-map';
import { markerConstants, markerRadiusColorConstants } from '../../constants/marker.constants';
import { getFsrCategoryLabel, getFsrCreatedBy } from '../../helpers/fieldSafetyReports';
import { getCachedAddress, setCachedAddress } from '../details-drawers/reverse-geocode-cache';

const NOTIFICATION_RADIUS_METERS = 50;

interface Props {
    report: ReportTypes.Model;
    onBack: () => void;
}

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Typography fontSize={12} fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
        </Typography>
        <Typography fontSize={14} fontWeight={400} mt={0.5}>
            {value ?? 'N/A'}
        </Typography>
    </Box>
);

export const FieldSafetyReportDetail: React.FC<Props> = ({ report, onBack }) => {
    const mapRef = useRef<google.maps.Map | null>(null);
    const center: LatLng = {
        lat: Number(report.latitude),
        lng: Number(report.longitude),
    };
    const [zoom] = useState<number>(17);

    const hasCoords = !Number.isNaN(center.lat) && !Number.isNaN(center.lng) && (center.lat !== 0 || center.lng !== 0);
    const categoryLabel = getFsrCategoryLabel(report.reportType);
    const radiusColor = markerRadiusColorConstants.report ?? '#081C63';

    const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);
    const [resolveError, setResolveError] = useState<string | null>(null);

    const reverseGeocodeKey = hasCoords
        ? `pom:reverse-geocode:${center.lat.toFixed(5)},${center.lng.toFixed(5)}`
        : null;

    useEffect(() => {
        setResolvedAddress(null);
        setResolveError(null);
    }, [report.id]);

    useEffect(() => {
        if (!hasCoords || !reverseGeocodeKey) return;

        let cancelled = false;
        const run = async () => {
            const cached = getCachedAddress(reverseGeocodeKey);
            if (cached) {
                if (!cancelled) setResolvedAddress(cached);
                return;
            }

            if (!window.google?.maps?.Geocoder) {
                if (!cancelled) setResolveError('Maps not loaded yet.');
                return;
            }

            try {
                const geocoder = new window.google.maps.Geocoder();
                const result = await geocoder.geocode({ location: center });
                const address = result.results?.[0]?.formatted_address ?? null;
                if (cancelled) return;
                if (!address) {
                    setResolveError('No address found for these coordinates.');
                    return;
                }
                setResolvedAddress(address);
                setCachedAddress(reverseGeocodeKey, address);
            } catch (err) {
                console.error('[FieldSafetyReportDetail] Reverse-geocode failed:', err);
                if (!cancelled) setResolveError('Could not look up address.');
            }
        };
        run();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [report.id, reverseGeocodeKey]);

    const addressNode = (() => {
        if (resolvedAddress) return resolvedAddress;
        if (!hasCoords) return 'N/A';
        if (resolveError) {
            return (
                <Typography component="span" fontSize={14} fontWeight={400} color="error">
                    {resolveError}
                </Typography>
            );
        }
        return (
            <Typography component="span" fontSize={14} fontWeight={400} color="text.secondary">
                Looking up address…
            </Typography>
        );
    })();

    return (
        <Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 3 }}>
                <Button
                    variant="outlined"
                    color="inherit"
                    onClick={onBack}
                    sx={{
                        border: 0,
                        padding: 1,
                        boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.24),0 16px 32px -4px rgba(145, 158, 171, 0.24)',
                    }}
                >
                    <ReportsBackIcon />
                    <Typography fontWeight={400} fontSize={14} color={'black'} marginLeft={1}>
                        Back
                    </Typography>
                </Button>
            </Box>

            <Card sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Chip label={categoryLabel} color="default" variant="outlined" />
                    <Chip label="Visible on mobile" color="success" variant="outlined" size="small" />
                </Stack>

                <Grid2 container spacing={4}>
                    <Grid2 size={{ md: 6, xs: 12, sm: 12 }}>
                        <Stack spacing={2.5}>
                            <Field label="Report name" value={report.name} />
                            <Field label="Created by" value={getFsrCreatedBy(report.sharedBy)} />
                            <Field label="Address" value={addressNode} />
                            <Field
                                label="Created at"
                                value={dayjs(report.createdAt).format('MM/DD/YYYY hh:mm A')}
                            />
                            <Field
                                label="Last updated"
                                value={dayjs(report.updatedAt).format('MM/DD/YYYY hh:mm A')}
                            />
                            <Field label="Comment" value={report.reportComment || '—'} />
                        </Stack>
                    </Grid2>

                    <Grid2 size={{ md: 6, xs: 12, sm: 12 }}>
                        <Typography fontWeight={500} mb={1.5}>
                            Location & notification radius
                        </Typography>
                        <When condition={hasCoords}>
                            <Box sx={{ width: '100%', height: 420, borderRadius: 1, overflow: 'hidden' }}>
                                <GoogleMap
                                    onLoad={(map) => {
                                        mapRef.current = map;
                                    }}
                                    options={{
                                        disableDefaultUI: true,
                                        zoomControl: true,
                                        fullscreenControl: true,
                                        mapTypeControl: false,
                                        streetViewControl: true,
                                        scaleControl: true,
                                        clickableIcons: false,
                                    }}
                                    mapContainerStyle={{ height: '100%', width: '100%' }}
                                    zoom={zoom}
                                    center={center}
                                >
                                    <Circle
                                        center={center}
                                        radius={NOTIFICATION_RADIUS_METERS}
                                        options={{
                                            fillColor: radiusColor,
                                            fillOpacity: 0.15,
                                            strokeColor: radiusColor,
                                            strokeOpacity: 0.6,
                                            strokeWeight: 2,
                                        }}
                                    />
                                    <MarkerF
                                        position={center}
                                        options={{
                                            icon: {
                                                url: markerConstants.report,
                                                scaledSize: new google.maps.Size(20, 20),
                                                anchor: new google.maps.Point(10, 10),
                                            },
                                        }}
                                    />
                                </GoogleMap>
                            </Box>
                            <Typography fontSize={12} color="text.secondary" mt={1}>
                                Caregivers within ~{NOTIFICATION_RADIUS_METERS}m of this pin receive a push notification.
                            </Typography>
                        </When>
                        <When condition={!hasCoords}>
                            <Typography color="text.secondary">No location available for this report.</Typography>
                        </When>
                    </Grid2>
                </Grid2>
            </Card>
        </Stack>
    );
};

export default FieldSafetyReportDetail;
